import type { AccountActivityDay, AccountStats } from "./account-api";

export type ActivityCalendarCell = AccountActivityDay & {
  inRange: boolean;
  level: 0 | 1 | 2 | 3 | 4;
};

export function parseCalendarDate(value: string): Date {
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year!, month! - 1, day);
}

export function calendarDate(value: Date): string {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function activityMinutes(playedMs: number): number {
  return playedMs > 0 ? Math.max(1, Math.round(playedMs / 60_000)) : 0;
}

export function activityLevel(playedMs: number): 0 | 1 | 2 | 3 | 4 {
  const minutes = activityMinutes(playedMs);
  if (minutes === 0) return 0;
  if (minutes < 15) return 1;
  if (minutes < 30) return 2;
  if (minutes < 60) return 3;
  return 4;
}

export function buildActivityCalendar(stats: AccountStats): ActivityCalendarCell[] {
  const activity = new Map(stats.activity.map((day) => [day.date, day]));
  const rangeStart = parseCalendarDate(stats.rangeStart);
  const rangeEnd = parseCalendarDate(stats.rangeEnd);
  const gridStart = new Date(rangeStart);
  gridStart.setDate(gridStart.getDate() - gridStart.getDay());
  const gridEnd = new Date(rangeEnd);
  gridEnd.setDate(gridEnd.getDate() + (6 - gridEnd.getDay()));
  const cells: ActivityCalendarCell[] = [];
  for (const cursor = new Date(gridStart); cursor <= gridEnd; cursor.setDate(cursor.getDate() + 1)) {
    const date = calendarDate(cursor);
    const inRange = cursor >= rangeStart && cursor <= rangeEnd;
    const day = activity.get(date);
    const playedMs = day?.playedMs ?? 0;
    cells.push({
      date,
      inRange,
      matchCount: day?.matchCount ?? 0,
      playedMs,
      level: inRange ? activityLevel(playedMs) : 0
    });
  }
  return cells;
}

export function monthLabelDates(cells: ActivityCalendarCell[]): Array<string | null> {
  const labels: Array<string | null> = [];
  let priorMonth = "";
  for (let index = 0; index < cells.length; index += 7) {
    const firstVisible = cells.slice(index, index + 7).find((cell) => cell.inRange);
    const month = firstVisible?.date.slice(0, 7) ?? priorMonth;
    if (firstVisible && month !== priorMonth) {
      const beginsLateInMonth = priorMonth === "" && Number(firstVisible.date.slice(-2)) > 7;
      labels.push(beginsLateInMonth ? null : firstVisible.date);
      priorMonth = month;
    } else {
      labels.push(null);
    }
  }
  return labels;
}

export function winRateLabel(wins: number, losses: number): string {
  const matches = wins + losses;
  return matches === 0 ? "—" : `${Math.round((wins / matches) * 100)}%`;
}

export function winLossRatioLabel(wins: number, losses: number): string {
  if (wins + losses === 0) return "—";
  if (losses === 0) return "∞";
  return (wins / losses).toFixed(2);
}

export function playedTimeLabel(playedMs: number): string {
  if (playedMs <= 0) return "0m";
  const minutes = Math.max(1, Math.round(playedMs / 60_000));
  if (minutes < 60) return `${minutes}m`;
  const hours = minutes / 60;
  return `${hours >= 10 ? hours.toFixed(1) : hours.toFixed(2).replace(/0$/, "")}h`;
}
